/**
 * BrandIcon
 *
 * Renderiza um ícone de marca a partir de:
 *   - Emoji:      "🤖"  → renderiza como texto
 *   - Simple Icons: "si:notion" → renderiza SVG do simple-icons na cor da marca
 *   - URL:        "https://..." ou "/" → renderiza <img>
 *
 * Uso:
 *   <BrandIcon icon="🤖" size={32} />
 *   <BrandIcon icon="si:notion" size={24} />
 *   <BrandIcon icon="si:whatsapp" size={20} color="#fff" />
 */

import React, { useMemo } from 'react';

// Converte slug "si:notion" → "siNotion"
function slugToKey(slug) {
  return 'si' + slug
    .split(/[-_]/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join('');
}

export default function BrandIcon({ icon, size = 24, color, style = {}, className }) {
  const resolved = useMemo(() => {
    if (!icon) return null;
    const str = typeof icon === 'string' ? icon.trim() : String(icon);

    // URL de imagem
    if (str.startsWith('http') || str.startsWith('/')) {
      return { type: 'img', src: str };
    }

    // Simple Icons — prefixo "si:"
    if (str.startsWith('si:')) {
      const slug = str.slice(3).toLowerCase().replace(/\s+/g, '');
      try {
        // Import dinâmico síncrono — simple-icons é um objeto enorme, importamos só o necessário
        // eslint-disable-next-line import/no-extraneous-dependencies
        const si = require('simple-icons');
        const key = slugToKey(slug);
        const iconData = si[key];
        if (iconData) {
          return { type: 'svg', path: iconData.path, hex: iconData.hex, title: iconData.title };
        }
      } catch {
        // fallback para emoji se simple-icons não disponível
      }
      return { type: 'emoji', value: '🔌' };
    }

    // Emoji ou texto
    return { type: 'emoji', value: str };
  }, [icon]);

  if (!resolved) return null;

  const baseStyle = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, ...style };

  if (resolved.type === 'img') {
    return (
      <img
        src={resolved.src}
        alt=""
        width={size}
        height={size}
        style={{ objectFit: 'contain', borderRadius: 4, ...style }}
        className={className}
      />
    );
  }

  if (resolved.type === 'svg') {
    const fill = color || `#${resolved.hex}`;
    return (
      <svg
        role="img"
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill={fill}
        style={baseStyle}
        className={className}
        aria-label={resolved.title}
      >
        <path d={resolved.path} />
      </svg>
    );
  }

  // Emoji
  return (
    <span
      style={{ fontSize: size * 0.85, lineHeight: 1, ...baseStyle }}
      className={className}
      aria-hidden="true"
    >
      {resolved.value}
    </span>
  );
}
